import Decimal from 'decimal.js';
import { isFiatCurrency } from './isFiatCurrency';
import { getCurrencyMinorMultiplier } from './getCurrencyMinorMultiplier';

export type MoneyType = {
  amount: Decimal.Value;
  currency: string;
};

export function isMoneyEquals(left?: MoneyType | null, right?: MoneyType | null) {
  return left && right ? left.amount === right.amount && left.currency === right.currency : false;
}

export function isMoneyPositive(money?: MoneyType | null): money is MoneyType {
  return money ? new Decimal(money.amount).gt(0) : false;
}

export function isMoneyZero(money?: MoneyType | null) {
  return money ? new Decimal(money.amount).eq(0) : true;
}

export function serverAmountToClientAmount(money: MoneyType | null | undefined) {
  if (!money) {
    return 0;
  }

  const multiplier = getCurrencyMinorMultiplier(money.currency);
  return new Decimal(money.amount).dividedBy(Math.pow(10, multiplier)).toNumber();
}

export function isSufficientBalance(
  clientAmount: Decimal.Value,
  balance: MoneyType | null | undefined,
) {
  return new Decimal(clientAmount).lte(serverAmountToClientAmount(balance));
}

export function clientAmountToServerAmount(money: MoneyType | null | undefined, round = true) {
  if (!money) {
    return 0;
  }

  const multiplier = getCurrencyMinorMultiplier(money.currency);
  const amount = new Decimal(money.amount).mul(Math.pow(10, multiplier));
  return round ? amount.round().toNumber() : amount.toNumber();
}

export function formatServerMoney(
  money: MoneyType | null | undefined,
  { locale = 'en-GB', withSign = false } = {},
) {
  if (!money) {
    return undefined;
  }

  const multiplier = getCurrencyMinorMultiplier(money.currency);
  const amount = new Decimal(money.amount).dividedBy(Math.pow(10, multiplier)).toNumber();

  const fiat = isFiatCurrency(money.currency);
  const format = Intl.NumberFormat(locale, {
    style: fiat ? 'currency' : 'decimal',
    currency: fiat ? money.currency : undefined,
    currencyDisplay: money.currency === 'USD' ? 'narrowSymbol' : 'symbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: multiplier,
    signDisplay: withSign ? 'exceptZero' : 'auto',
  });

  const formatted = format.format(amount);
  return fiat ? formatted : `${money.currency} ${formatted}`;
}

export function formatRate(money: MoneyType, { locale = 'en-GB' } = {}) {
  const amount = new Decimal(money.amount).toNumber();

  const fiat = isFiatCurrency(money.currency);
  const format = Intl.NumberFormat(locale, {
    style: fiat ? 'currency' : 'decimal',
    currency: fiat ? money.currency : undefined,
    currencyDisplay: money.currency === 'USD' ? 'narrowSymbol' : 'symbol',
    maximumFractionDigits: 10,

    signDisplay: 'auto',
  });

  const formatted = format.format(amount);
  return fiat ? formatted : `${money.currency} ${formatted}`;
}

export function mapStrangeServerMoneyToServerMoney(money: {
  currency: string;
  minorAmount: number;
}) {
  if ('amount' in money && typeof money.amount === 'string') {
    return mapClientMoneyToServerMoney({ amount: money.amount, currency: money.currency });
  }

  if ('amount' in money && typeof money.amount === 'number') {
    return {
      amount: money.amount,
      currency: money.currency,
    };
  }

  return {
    amount: money.minorAmount,
    currency: money.currency,
  };
}

export function mapClientMoneyToServerMoney(money: MoneyType, round = true) {
  return {
    amount: clientAmountToServerAmount(money, round),
    currency: money.currency,
  };
}

export function mapServerMoneyToClientMoney(money: MoneyType) {
  return {
    amount: String(serverAmountToClientAmount(money)),
    currency: money.currency,
  };
}

export function mapServerMoneyToStrangeServerMoney(money: MoneyType) {
  return {
    amount: String(serverAmountToClientAmount(money)),
    minorAmount: new Decimal(money.amount).toNumber(),
    currency: money.currency,
  };
}

const currencyMap: Record<string, string> = {
  BTC: 'Bitcoin',
  EURT: 'Tether EUR',
  EURA: 'Angle EUR',
  USDT: 'Tether USD',
  USDC: 'USD Coin',
  TRX: 'Tron',
  ETH: 'Ethereum',
  BNB: 'Bininace coin',
  MATIC: 'Polygon',
  POL: 'Polygon (ex-Matic)',
  ARB: 'Arbitrum',
  ADA: 'Cardano',
  XTZ: 'Tezos',
  ATOM: 'Cosmos',
  ISLM: 'Islamic Coin',
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  AED: 'UAE Dirham',
  CHF: 'Swiss Franc',
  CNY: 'Chinese Yuan',
  XRP: 'XRP',
  NPC: 'Non-Playable Coin',
  DOGE: 'Dogecoin',
  PEPE: 'Pepe',
  SHIB: 'Shiba Inu',
  TRUMP: 'OFFICIAL TRUMP',
};

export function mapCurrencyToFullName(currency: string) {
  return currencyMap[currency] ?? currency;
}

const networkMap: Record<string, string> = {
  ERC20: 'Ethereum ERC20',
  TRC20: 'Tron TRC20',
  BEP20: 'BNB Smart Chain',
  POLYGON: 'Polygon',
  ARBITRUM: 'Arbitrum',
  CARDANO: 'Cardano',
  TEZOS: 'Tezos',
  BTC_LEGACY: 'Bitcoin Legacy',
  BTC_SEGWIT: 'Bitcoin Segwit',
  HAQQ: 'Haqq',
  SOLANA: 'Solana',
  TON: 'TON',
  XRPL: 'XRP Ledger',
  COSMOS: 'Cosmos',
  SEPA: 'SEPA',
  SWIFT: 'SWIFT',
  SEPA_INSTANT: 'SEPA Instant',
  FASTER_PAYMENTS: 'Faster Payments',
};

export function mapNetworkToFullName(network: string) {
  return networkMap[network] ?? network;
}
